import {
    useCallback,
    useMemo,
    useState,
} from 'react'
import {
    useQueryClient,
} from '@tanstack/react-query'

import FormWithControls from 'app/shared-components/FormWithControls'
import requests from 'app/requests/Order/OrderBilling.requests'
import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import StatusHandler from 'app/shared-components/StatusHandler'
import SharedContextProvider from 'app/shared-components/SharedContext'
import TruckingCapacity from 'app/shared-components/TruckingCapacity'
import ButtonContainer from 'app/shared-components/ButtonContainer'
import Button from 'app/shared-components/Button'
import useHasPermission from 'app/hooks/useHasPermission'
import Fees from 'app/shared-components/Fees'
import {
    FeesType,
} from 'app/enums/common.enums'
import Card from 'app/shared-components/Card'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import {
    InvoiceDesignation,
    InvoiceStatus,
} from 'app/enums/label.enums'
import useEnumValues from 'app/hooks/useEnumValues'
import useGetBillingInformation from 'app/Apps/OrderManagement/Orders/CustomerTransport/hooks/useGetBillingInformation'
import {
    useOrdersContext,
} from 'app/Apps/OrderManagement/Orders/CustomerTransport/OrdersContext'
import {
    OutgoingInvoice,
} from 'app/Apps/OrderManagement/Orders/orders.types'
import useGetBillingCompany from 'app/Apps/ContactManagement/BillingCompanies/hooks/useGetBillingCompany'

import useGetFeesInfo from './hooks/useGetFeesInfo'
import InvoiceSelector from './InvoiceSelector'
import getInvoiceFields from './invoices.fields'
import getOrderFields from './order.fields'
import useStyles from './Invoices.style'
import Positions from './Positions'

import RelinkLaneFeesDialog from './RelinkLaneFeesDialog'
import RelinkAdditionalFeesDialog from './RelinkAdditionalFeesDialog'
import useSendToAbacus from './hooks/useSendToAbacus'
import useSendToSap from './hooks/useSendToSap'
import useGetFilterAllOutgoingInvoices from './hooks/useGetFilterAllOutgoingInvoices'

const DOMAIN_NAME = 'OrderBillingInformationView'

const getSelectedInvoice = (
    outgoingInvoices: OutgoingInvoice[], selectedInvoiceId: number|undefined,
): OutgoingInvoice | undefined => {
    if (selectedInvoiceId === undefined) {
        return undefined
    }

    const selectedInvoice = outgoingInvoices?.find(({
        id,
    }) => {
        return id === selectedInvoiceId
    })

    if (!selectedInvoice) {
        return outgoingInvoices[0]
    }

    return selectedInvoice
}

const Invoices = ({
    id: orderNumber,
}: { id: string }) => {
    const queryClient = useQueryClient()
    const getAllowedValues = useEnumValues()

    const {
        billingInformation,
        isFetching,
        isError,
        error,
    } = useGetBillingInformation(orderNumber)

    const {
        data: outgoingInvoices = [] as OutgoingInvoice[],
        isDataReady: isDataReadyOutgoingInvoices,
        isFetching: isFetchingOutgoingInvoices,
        invalidate,
    } = useGetFilterAllOutgoingInvoices(orderNumber)

    const {
        selectedInvoiceId,
        setSelectedInvoiceId,
    } = useOrdersContext()

    const selectedInvoice = useMemo(() => {
        return getSelectedInvoice(outgoingInvoices, selectedInvoiceId)
    }, [
        outgoingInvoices,
        selectedInvoiceId,
    ])

    const {
        data: billingCompany,
    } = useGetBillingCompany(selectedInvoice?.billingCompany?.id)

    const preparedBillingInformation = useMemo(() => {
        // we need to override billingInformation.billing by billingCompany
        // taken selectedInvoice?.billingCompany?.id by request
        const preparedInfo = {
            ...billingInformation,
            billing: {
                ...billingInformation?.billing,
                billingCompany: billingCompany || billingInformation?.billing?.billingCompany,
            },
        }

        return preparedInfo
    }, [
        billingInformation,
        billingCompany,
    ])

    const [
        openRelinkLaneFees,
        setRelinkLaneFeesOpen,
    ] = useState<boolean>(false)

    const [
        openRelinkAdditionalFees,
        setRelinkAdditionalFeesOpen,
    ] = useState<boolean>(false)

    const outgoingInvoiceUpdate = useHasPermission(['outgoing_invoice_update'])
    const hasPermissionToSendInvoiceToSap = useHasPermission(['sap_send_invoice'])
    const invoiceFeesUpdate: boolean = useHasPermission(['invoice_fees_update'])

    const onSuccess = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: [
                RequestKeys.getBillingInformation,
                {
                    orderNumber,
                },
            ],
        })
    }, [
        orderNumber,
        queryClient,
    ])

    const {
        classes,
    } = useStyles()

    const onSuccessCreate = useCallback((params: OutgoingInvoice): void => {
        setSelectedInvoiceId(params?.id)
        invalidate()
        onSuccess()
    }, [
        onSuccess,
        invalidate,
        setSelectedInvoiceId,
    ])

    const {
        laneFees,
        additionalFees,
    } = useGetFeesInfo({
        orderNumber,
        selectedInvoice,
    })

    const onSuccessRelinkLaneFees = () => {
        queryClient.invalidateQueries({
            queryKey: [
                RequestKeys.getLaneFees,
                {
                    orderNumber,
                },
            ],
        })

        onSuccess()
    }

    const onSuccessRelinkAdditionalFees = () => {
        queryClient.invalidateQueries({
            queryKey: [
                RequestKeys.getAdditionalFees,
                {
                    orderNumber,
                },
            ],
        })

        onSuccess()
    }

    const onSelectInvoice = useCallback((invoice: OutgoingInvoice): void => {
        setSelectedInvoiceId(invoice?.id)
    }, [setSelectedInvoiceId])

    const disabledInvoice: boolean = selectedInvoice?.invoiceStatus !== InvoiceStatus.CREATED

    const orderFields = useFieldsWithClassName(getOrderFields({
        baseLeaseDays: billingInformation?.billing?.baseLeaseDays,
        extraDays: billingInformation?.billing?.extraDays,
        laneFeesCommentForBilling: billingInformation
            ?.billing.laneFeesCommentForBilling,
        additionalFeesCommentForBilling: billingInformation
            ?.billing.additionalFeesCommentForBilling,
        laneFees,
        additionalFees,
        paymentTerms: billingCompany?.paymentTerms,
        instructionsForBilling: billingCompany?.instructionsForBilling,
        hasBulkInvoice: billingCompany?.hasBulkInvoice,
        getAllowedValues,
    }), classes)

    const modifyDataBeforeSend = useCallback(
        (requestData: OutgoingInvoice): Partial<OutgoingInvoice> => {
            return {
                invoiceDate: requestData.invoiceDate,
                invoiceNormalAddon: requestData.invoiceNormalAddon,
                invoiceBulkOrIndividual: requestData.invoiceBulkOrIndividual,
                invoiceTitle: requestData.invoiceTitle,
                invoiceComment: requestData.invoiceComment,
                laneInfo: requestData.laneInfo,
                creditNoteOriginalInvoice: requestData.creditNoteOriginalInvoice,
                creditNoteReason: requestData.creditNoteReason,
            }
        }, [],
    )

    const updateInvoiceParams = useMemo(() => {
        return requests.updateGeneralInformation({
            outgoingInvoiceId: selectedInvoice?.id,
        })
    }, [selectedInvoice?.id])

    const [
        sendingToAbacus,
        setSendingToAbacus,
    ] = useState(false)

    const onAbacusSendError = useCallback(() => {
        setSendingToAbacus(false)
    }, [])

    const onAbacusSendSuccess = useCallback(() => {
        setSendingToAbacus(false)
        onSuccess()
    }, [onSuccess])

    const {
        mutate: sendToAbacusRequest,
    } = useSendToAbacus({
        onSuccess: onAbacusSendSuccess,
        onError: onAbacusSendError,
    })

    const {
        mutate: sendToSapRequest,
        isPending,
    } = useSendToSap({
        onSuccess,
    })

    const sendToAbacus = useCallback((): void => {
        setSendingToAbacus(true)
        sendToAbacusRequest({
            selectedInvoiceId: selectedInvoice?.id,
        })
    }, [
        selectedInvoice?.id,
        sendToAbacusRequest,
    ])

    const sendToSap = useCallback((): void => {
        sendToSapRequest({
            selectedInvoiceId: selectedInvoice?.id,
        })
    }, [
        selectedInvoice?.id,
        sendToSapRequest,
    ])

    const showSendToSapButton = hasPermissionToSendInvoiceToSap
        && selectedInvoice?.invoiceStatus
        && selectedInvoice?.invoiceStatus === InvoiceStatus.CREATED

    const originalInvoiceAllowedValues = useMemo(() => {
        return outgoingInvoices?.reduce((acc, element) => {
            if (element.invoiceDesignation !== InvoiceDesignation.CREDIT_NOTE) {
                const additionalInfo = element?.sapSalesOrderId || element?.abacusOrderNumber || ''

                return {
                    ...acc,
                    [element.id]: `${element.invoiceTitle}${additionalInfo ? ', ' : ''}${additionalInfo}`,
                }
            }
            return acc
        }, {})
    }, [outgoingInvoices])

    const disabledForm = !outgoingInvoiceUpdate || disabledInvoice
    const invoiceFields = getInvoiceFields({
        getAllowedValues,
        isCreditNote: selectedInvoice?.invoiceDesignation === InvoiceDesignation.CREDIT_NOTE,
        originalInvoiceAllowedValues,
    })

    return (
        <StatusHandler
            isSuccess={isDataReadyOutgoingInvoices && Boolean(billingInformation)}
            isFetching={isFetching || isFetchingOutgoingInvoices}
            isError={isError}
            error={error}
        >
            <div>
                <InvoiceSelector
                    orderNumber={orderNumber}
                    invoices={outgoingInvoices}
                    selectedInvoice={selectedInvoice}
                    setSelectedInvoice={onSelectInvoice}
                    onSuccess={onSuccessCreate}
                    disabled={!outgoingInvoiceUpdate}
                />
                <DomainObjectForm
                    name={DOMAIN_NAME}
                    value={preparedBillingInformation}
                    fields={orderFields}
                    className={classes.gridWrapperOrder}
                    disabled
                />
                {selectedInvoice && (
                    <>
                        <FormWithControls
                            name="OutgoingInvoiceView"
                            value={selectedInvoice}
                            fields={invoiceFields}
                            classNames={{
                                gridWrapper: disabledForm
                                    ? classes.gridWrapperInvoiceDisabled
                                    : classes.gridWrapperInvoice,
                            }}
                            onSuccess={onSuccess}
                            disabled={disabledForm}
                            modifyDataBeforeSend={modifyDataBeforeSend}
                            requestParams={updateInvoiceParams}
                            exists
                        />
                        <Positions
                            invoiceId={selectedInvoice.id}
                            currency={selectedInvoice?.currency}
                            disabled={disabledForm}
                            canBeDeleted={
                                selectedInvoice.invoiceStatus !== InvoiceStatus.SENTTOBILLINGCOMPANY
                            }
                        />
                        <ButtonContainer>
                            <Button
                                label="Send to Abacus"
                                data-testid="send-to-abacus"
                                onClick={sendToAbacus}
                                disabled={sendingToAbacus}
                            />
                            {showSendToSapButton && (
                                <Button
                                    label="Send to Sap"
                                    data-testid="send-to-sap"
                                    onClick={sendToSap}
                                    saving={isPending}
                                />
                            )}
                        </ButtonContainer>
                        <Card
                            fullHeight
                            data-testid="truckingCapacity"
                            className={classes.truckingCapacity}
                            title="Max Containers Per Truck / Van"
                        >
                            <TruckingCapacity
                                value={laneFees?.truckingCapacity}
                                disabled
                            />
                        </Card>
                        <Card
                            fullHeight
                            data-testid="position-card"
                            className={classes.laneFees}
                            title="Lane Fees"
                        >
                            <Fees
                                value={laneFees?.fees}
                                currency={laneFees?.contractBasisCurrency}
                                disabled
                            />
                        </Card>
                        {invoiceFeesUpdate
                            && selectedInvoice.invoiceStatus === InvoiceStatus.CREATED
                            && (
                                <ButtonContainer>
                                    <Button
                                        label="relink lane fees"
                                        onClick={() => {
                                            setRelinkLaneFeesOpen(true)
                                        }}
                                        secondary
                                        data-testid="relink-lane-fees"
                                    />
                                </ButtonContainer>
                            )}
                        <Card
                            fullHeight
                            data-testid="position-card"
                            className={classes.laneFees}
                            title="Additional Fees"
                        >
                            <SharedContextProvider
                                value={{
                                    feePricingName: FeesType.Additional,
                                }}
                            >
                                <Fees
                                    value={additionalFees?.fees}
                                    currency={additionalFees?.contractBasisCurrency}
                                    disabled
                                />
                            </SharedContextProvider>
                        </Card>
                        {invoiceFeesUpdate
                            && selectedInvoice.invoiceStatus === InvoiceStatus.CREATED && (
                            <ButtonContainer>
                                <Button
                                    label="relink additional fees"
                                    onClick={() => {
                                        setRelinkAdditionalFeesOpen(true)
                                    }}
                                    secondary
                                    data-testid="relink-additional-fees"
                                />
                            </ButtonContainer>
                        )}
                    </>
                )}
                <RelinkLaneFeesDialog
                    open={openRelinkLaneFees}
                    setOpen={setRelinkLaneFeesOpen}
                    onSuccess={onSuccessRelinkLaneFees}
                    orderNumber={orderNumber}
                />
                <RelinkAdditionalFeesDialog
                    open={openRelinkAdditionalFees}
                    setOpen={setRelinkAdditionalFeesOpen}
                    onSuccess={onSuccessRelinkAdditionalFees}
                    orderNumber={orderNumber}
                />
            </div>
        </StatusHandler>
    )
}

export default Invoices
